import React, { useState } from "react";
import { Menu, MenuItem, ListItemIcon, Select, TextField, Tooltip } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import PestControlRoundedIcon from "@mui/icons-material/PestControlRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import BackgroundLetterAvatarsLogo from "../AvatarLogo";
import { assigneeOf, childTypesOf, isDoneStatus, rollupSegments } from "./treeUtils";

const TYPE_ICONS = {
  epic: BoltRoundedIcon,
  story: BookmarkRoundedIcon,
  task: TaskAltRoundedIcon,
  bug: PestControlRoundedIcon,
};

export const TypeIcon = ({ type }) => {
  const key = String(type || "story").toLowerCase();
  const Icon = TYPE_ICONS[key] || BookmarkRoundedIcon;
  return (
    <span className={`wh-type wh-type--${TYPE_ICONS[key] ? key : "story"}`} title={type || "Story"} aria-label={type || "Story"}>
      <Icon />
    </span>
  );
};

/* a parent's children as one bar: done, in progress, to do */
export const RollupBar = ({ rollup }) => {
  const segments = rollupSegments(rollup);
  if (!segments.length) return <span className="wh-muted">No child issues</span>;
  const label = segments.map((s) => `${s.count} ${s.label.toLowerCase()}`).join(" · ");
  return (
    <Tooltip title={`${label} — ${rollup.total} in total`}>
      <span className="wh-rollup" role="img" aria-label={`${label} of ${rollup.total}`}>
        {segments.map((s) => (
          <span key={s.key} className={`wh-rollup__seg is-${s.key}`} style={{ width: `${s.percent}%` }} />
        ))}
      </span>
    </Tooltip>
  );
};

const StatusSelect = ({ issue, statuses, onChange, disabled }) => {
  const options = statuses.includes(issue.status) || !issue.status ? statuses : [issue.status, ...statuses];
  return (
    <Select
      size="small"
      value={issue.status || ""}
      onChange={(e) => onChange(issue, e.target.value)}
      disabled={disabled}
      className={`wh-status ${isDoneStatus(issue.status) ? "is-done" : ""}`}
      IconComponent={KeyboardArrowDownRoundedIcon}
      inputProps={{ "aria-label": `Status of ${issue.name}` }}
      MenuProps={{ slotProps: { paper: { className: "wh-status-menu" } } }}
      renderValue={(value) => <span className="wh-status__text">{value || "—"}</span>}
    >
      {options.map((status) => (
        <MenuItem key={status} value={status}>{status}</MenuItem>
      ))}
    </Select>
  );
};

/* the inline "new child" row, under its parent */
const NewChildRow = ({ depth, types, busy, onCreate, onCancel }) => {
  const [summary, setSummary] = useState("");
  const [type, setType] = useState(types[0]);
  const submit = () => summary.trim() && onCreate({ summary: summary.trim(), issueType: type });
  return (
    <div className="wh-row wh-row--new" role="row" aria-level={depth + 1}>
      <div className="wh-cell wh-cell--work" role="gridcell" style={{ "--depth": depth }}>
        <span className="wh-chevron-spacer" />
        <Select
          size="small"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="wh-new-type"
          inputProps={{ "aria-label": "New issue type" }}
          renderValue={(value) => <><TypeIcon type={value} />{value}</>}
        >
          {types.map((t) => <MenuItem key={t} value={t}><TypeIcon type={t} />&nbsp;{t}</MenuItem>)}
        </Select>
        <TextField
          size="small"
          autoFocus
          fullWidth
          placeholder={`What needs to be done? Press Enter to add the ${type.toLowerCase()}`}
          value={summary}
          disabled={busy}
          onChange={(e) => setSummary(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); submit(); }
            if (e.key === "Escape") onCancel();
          }}
          inputProps={{ "aria-label": `New ${type} summary` }}
        />
      </div>
      <div className="wh-cell wh-cell--actions-new" role="gridcell">
        <button type="button" className="wh-btn is-primary" onClick={submit} disabled={busy || !summary.trim()}>Add</button>
        <button type="button" className="wh-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
};

/**
 * The Epic → Story → Task table. Purely presentational: rows come in already
 * flattened (see visibleRows) and every change goes out through a callback.
 */
const WorkHierarchyTable = ({
  rows,
  statuses,
  busyIds = new Set(),
  addingUnder,
  creating,
  onToggle,
  onOpen,
  onStatusChange,
  onAddChild,
  onCreateChild,
  onCancelAdd,
  onChangeParent,
  onRemoveParent,
}) => {
  const [menu, setMenu] = useState(null);
  const close = () => setMenu(null);

  const rendered = [];
  if (addingUnder === "root") {
    rendered.push(
      <NewChildRow key="new-root" depth={0} types={["Epic", "Story", "Task", "Bug"]} busy={creating} onCreate={(v) => onCreateChild(null, v)} onCancel={onCancelAdd} />
    );
  }

  rows.forEach(({ node, hasChildren, open }, index) => {
    const id = String(node._id);
    const done = isDoneStatus(node.status);
    const person = assigneeOf(node);
    const startsGroup = node.depth === 0 && index > 0;

    rendered.push(
      <div
        key={id}
        className={`wh-row ${startsGroup ? "starts-group" : ""} ${node.isContext ? "is-context" : ""}`}
        role="row"
        aria-level={node.depth + 1}
        aria-expanded={hasChildren ? open : undefined}
        data-testid={`row-${node.name}`}
      >
        <div className="wh-cell wh-cell--work" role="gridcell" style={{ "--depth": node.depth }}>
          {hasChildren ? (
            <button
              type="button"
              className={`wh-chevron ${open ? "is-open" : ""}`}
              onClick={() => onToggle(id)}
              aria-label={`${open ? "Collapse" : "Expand"} ${node.name}`}
            >
              <ChevronRightRoundedIcon />
            </button>
          ) : (
            <span className="wh-chevron-spacer" />
          )}
          <TypeIcon type={node.issueType} />
          <button type="button" className={`wh-key ${done ? "is-done" : ""}`} onClick={() => onOpen(node)}>
            {node.name}
          </button>
          <button type="button" className="wh-summary" onClick={() => onOpen(node)} title={node.summary}>
            {node.summary}
          </button>
          {hasChildren ? <span className="wh-child-count" title={`${node.children.length} direct child issues`}>{node.children.length}</span> : null}
        </div>

        <div className="wh-cell wh-cell--status" role="gridcell">
          {hasChildren ? (
            <RollupBar rollup={node.rollup} />
          ) : (
            <StatusSelect issue={node} statuses={statuses} onChange={onStatusChange} disabled={busyIds.has(id)} />
          )}
        </div>

        <div className="wh-cell wh-cell--assignee" role="gridcell">
          {person ? (
            <>
              <BackgroundLetterAvatarsLogo LogoTitle={person.employeeName || person.employeeEmail} RequiredFontSize="11px !important" />
              <span className="wh-ellipsis" title={person.employeeName || person.employeeEmail}>{person.employeeName || person.employeeEmail}</span>
            </>
          ) : (
            <>
              <span className="wh-avatar-empty"><PersonOutlineRoundedIcon /></span>
              <span className="wh-muted">Unassigned</span>
            </>
          )}
        </div>

        <div className="wh-cell wh-cell--sprint" role="gridcell">
          {node.sprint ? (
            <span className={`wh-sprint ${node.sprint.active ? "is-active" : ""} ${node.sprint.completed ? "is-completed" : ""}`} title={node.sprint.name}>
              {node.sprint.active ? <span className="wh-sprint__dot" /> : null}
              <span className="wh-ellipsis">{node.sprint.name}</span>
            </span>
          ) : (
            <span className="wh-muted">Backlog</span>
          )}
        </div>

        <div className="wh-cell wh-cell--menu" role="gridcell">
          <button
            type="button"
            className="wh-more"
            aria-label={`Actions for ${node.name}`}
            aria-haspopup="menu"
            onClick={(e) => setMenu({ anchor: e.currentTarget, node })}
          >
            <MoreHorizRoundedIcon />
          </button>
        </div>
      </div>
    );

    if (addingUnder === id) {
      rendered.push(
        <NewChildRow
          key={`new-${id}`}
          depth={node.depth + 1}
          types={childTypesOf(node.issueType)}
          busy={creating}
          onCreate={(values) => onCreateChild(node, values)}
          onCancel={onCancelAdd}
        />
      );
    }
  });

  const menuNode = menu?.node;
  const childTypes = menuNode ? childTypesOf(menuNode.issueType) : [];

  return (
    <div className="wh-table" role="treegrid" aria-label="Work items" aria-rowcount={rows.length}>
      <div className="wh-row wh-row--head" role="row">
        <div className="wh-cell wh-cell--work" role="columnheader">Work</div>
        <div className="wh-cell wh-cell--status" role="columnheader">Status</div>
        <div className="wh-cell wh-cell--assignee" role="columnheader">Assignee</div>
        <div className="wh-cell wh-cell--sprint" role="columnheader">Sprint</div>
        <div className="wh-cell wh-cell--menu" role="columnheader"><span className="visually-hidden">Actions</span></div>
      </div>

      {rendered}

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={close} slotProps={{ paper: { className: "wh-menu" } }}>
        <MenuItem onClick={() => { onOpen(menuNode); close(); }}>
          <ListItemIcon><OpenInNewRoundedIcon fontSize="small" /></ListItemIcon>Open
        </MenuItem>
        {childTypes.length ? (
          <MenuItem onClick={() => { onAddChild(menuNode); close(); }}>
            <ListItemIcon><AddRoundedIcon fontSize="small" /></ListItemIcon>
            Add {childTypes[0].toLowerCase()}
          </MenuItem>
        ) : null}
        {String(menuNode?.issueType).toLowerCase() !== "epic" ? (
          <MenuItem onClick={() => { onChangeParent(menuNode); close(); }}>
            <ListItemIcon><AccountTreeRoundedIcon fontSize="small" /></ListItemIcon>
            {menuNode?.parent ? "Change parent…" : "Move under…"}
          </MenuItem>
        ) : null}
        {menuNode?.parent ? (
          <MenuItem onClick={() => { onRemoveParent(menuNode); close(); }}>
            <ListItemIcon><LinkOffRoundedIcon fontSize="small" /></ListItemIcon>Remove parent
          </MenuItem>
        ) : null}
      </Menu>
    </div>
  );
};

export default WorkHierarchyTable;
